// dynamic-form/index.js
// -----------------------------------------------------------------------------


// Import General libraries
import React, {useEffect, useState, forwardRef, useImperativeHandle, memo} from 'react'
import hasValue from './util/hasValue'
import validationNotifier from './util/validationNotifier'
import * as Yup from 'yup'
import {v4 as uuidv4} from 'uuid';

// Import Elements
import Elements from './elements'

// Main Component: DynamicForm
const DynamicForm = memo(
	forwardRef((props, ref) => {
		const {items = [], style, onChange, size = 'md', defaultValues = {}, validOnChange = false} = props
		
		// Initial default value of form data.
		const [dynamicFormData, setDynamicFormData] = useState({})
		const [dynamicFormBoundData, setDynamicFormBoundData] = useState({})
		const [dynamicFormValidation, setDynamicFormValidation] = useState({})
		const [innerItem, setInnerItem] = useState([])
		const [forceItemToUpdate, setForceItemToUpdate] = useState({})
		const [formId, setFormId] = useState(uuidv4())
		
		const initialDynamicForm = (itemsFn) => {
			const initItems = itemsFn(defaultValues, {}, {}, {})

			// Crete initial data based on items.
			const initData = initItems.reduce((acc, {name, defaultValue}) => {
				acc[name] = defaultValue ? defaultValue : null
				return acc
			}, {})

			// Create initial validation based on items.
			const initValidation = initItems.reduce((acc, {name, validation = null}) => {
				acc[name] = validation
				return acc
			}, {})

			// Set initial data and validation to seperated state.
			setInnerItem(itemsFn(defaultValues, initData, dynamicFormBoundData, initValidation))
			setDynamicFormData(initData)
			setDynamicFormValidation(initValidation)
		}

		useEffect(() => {
			initialDynamicForm(items)
		}, [items, setDynamicFormData, setDynamicFormValidation])
		
		useEffect(() => {
			if (validOnChange) {
				handleFormValidation(innerItem, dynamicFormData)
			}
		}, [dynamicFormData])
		
		// Handle Validation
		const handleFormValidation = async (items, dynamicFormData) => {
			const validationErrors = {}
			
			for (const {name, validation} of items) {
				if (validation) {
					const validator = Yup.object().shape({[name]: validation})
					const innerData = hasValue(dynamicFormData?.[name]) ? dynamicFormData[name] : ''
					await validator
					.validate({[name]: innerData})
					.then(() => {
						validationErrors[name] = null
					})
					.catch((err) => {
						validationErrors[name] = err.message.substring(0, 50) + (err.message.length > 50 ? '...' : '')
					})
				}
			}
			setDynamicFormValidation(validationErrors)
			return validationErrors
		}
		
		const handleValidateItem = async (value, name) => {
			const item = innerItem.find((item) => item.name === name)
			const validation = item.validation
			const validator = Yup.object().shape({[item.name]: validation})
			const innerValue = value ? value : ''
			let validationError = null
			await validator
			.validate({[item.name]: innerValue})
			.then(() => {
				validationError = null
			})
			.catch((err) => {
				validationError = err.message.substring(0, 50) + (err.message.length > 50 ? '...' : '')
			})
			const newValidation = {...dynamicFormValidation, [item.name]: validationError}
			setDynamicFormValidation(newValidation)
			
			return validationError
		}
		// Handle form change
		const handleChange = async (newData, name, bound, validate, force) => {
			if (force) {
				setForceItemToUpdate((PrevState) => ({...PrevState, [name]: PrevState[name] ? PrevState[name] + 1 : 1}))
			}
			if (
				JSON.stringify(newData) !== JSON.stringify(dynamicFormData[name]) ||
				newData.length !== dynamicFormData[name]?.length
			) {
				const inlineDynamicFormData = {...dynamicFormData, [name]: newData}
				const inlineDynamicFormBoundData = {...dynamicFormBoundData, [name]: bound}
				const inlineItemChange = {data: dynamicFormData, bound: dynamicFormBoundData}
				
				// handle state for value
				await setDynamicFormData((prevState) => ({
					...prevState,
					[name]: newData,
				}))
				inlineItemChange.data = {...dynamicFormData, [name]: newData}
				
				if (bound) {
					if (dynamicFormBoundData[name]?.id !== bound?.id) {
						await setDynamicFormBoundData((prevState) => ({
							...prevState,
							[name]: bound,
						}))
					}
					inlineItemChange.bound = {
						...dynamicFormBoundData,
						[name]: bound,
					}
				}
				if (onChange) onChange(inlineDynamicFormData, inlineDynamicFormBoundData)
				if (validate) await handleFormValidation(innerItem, inlineDynamicFormData)
				handleFormValidation(innerItem, inlineDynamicFormData)
				setInnerItem(items(defaultValues, inlineItemChange.data, inlineItemChange.bound, dynamicFormValidation))
			}
		}
		
		const handleBindData = (data, name) => {
			setDynamicFormBoundData((prevState) => ({
				...prevState,
				[name]: data,
			}))
		}
		
		const handleIsFile = (key, value) => {
			return value
		}
		
		// Handle Submit Form
		useImperativeHandle(ref, () => ({
			// initial function to get data from dynamic form in parent component
			getData: async (withoutValidation) => {
				const validationErrors = await handleFormValidation(innerItem, dynamicFormData)
				if (hasValue(validationErrors) && !withoutValidation) return false
				
				const generateFormData = {}
				Object.entries(dynamicFormData).forEach(([key, value]) => {
					// handle nested object
					if (key.includes(':')) {
						// handle nested object
						const mainKey = key.split(':')[0]
						const subKey = key.split(':')[1]
						generateFormData[mainKey] = {
							...generateFormData[mainKey],
							[subKey]: handleIsFile(subKey, value),
						}
					} else {
						generateFormData[key] = handleIsFile(key, value)
					}
				})
				return generateFormData
			},
			validate: async (notification) => {
				const validationErrors = await handleFormValidation(innerItem, dynamicFormData)
				const validationErrorsMessage = Object.fromEntries(
					Object.entries(validationErrors).filter(([key, value]) => value !== null)
				)
				const isValid = !hasValue(validationErrors)
				if (notification && !isValid) {
					validationNotifier(validationErrorsMessage)
				}
				return {isValid: isValid, validationErrors: validationErrorsMessage}
			},
			setData2: (newData, force) => {
				if (newData) {
					const newFormData = {}
					const hasData = innerItem
					.map((item) => {
						return newData[item.name] !== null && newData[item.name] !== undefined && item.type !== 'Hidden'
					})
					.includes(true)
					innerItem.map((item) => {
						newFormData[item.name] = hasData
							? force
								? newData[item.name] !== null && newData[item.name] !== undefined
									? newData[item.name]
									: item.defaultValues || ''
								: //newData[item.name]
								newData[item.name] !== null && newData[item.name] !== undefined
									? newData[item.name]
									: dynamicFormData[item.name] || ''
							: item.defaultValue || ''
						
						if (item.type === 'Select')
							setForceItemToUpdate((PrevState) => ({
								...PrevState,
								[name]: PrevState[name] ? PrevState[name] + 1 : 1
							}))
					})
					setDynamicFormData({...newFormData})
				} else {
					setDynamicFormData((prevState) => {
						const newState = {...prevState}
						Object.keys(newState).forEach((key) => (newState[key] = ''))
						return newState
					})
				}
				handleFormValidation(innerItem, newData)
			},
			setData: (newData, force) => {
				if (newData) {
					const newFormData = {}
					
					const hasDataList = innerItem.map((item) => {
						return (
							newData[item.name] !== null &&
							newData[item.name] !== undefined &&
							newData[item.name] !== '' &&
							item.type !== 'Hidden'
						)
					})
					const hasData = hasDataList.includes(true)
					innerItem.map((item) => {
						newFormData[item.name] = hasData
							? force
								? newData[item.name] !== null && newData[item.name] !== undefined
									? newData[item.name]
									: item.defaultValues || ''
								: newData[item.name] !== null && newData[item.name] !== undefined
									? newData[item.name]
									: dynamicFormData[item.name] || ''
							: newData[item.name] || item.defaultValue || ''
						if (item.type === 'Select')
							setForceItemToUpdate((PrevState) => ({
								...PrevState,
								[name]: PrevState[name] ? PrevState[name] + 1 : 1
							}))
					})
					setDynamicFormData({...newFormData})
				} else {
					setDynamicFormData((prevState) => {
						const newState = {...prevState}
						Object.keys(newState).forEach((key) => (newState[key] = null))
						return newState
					})
				}
				
				handleFormValidation(innerItem, newData)
			},
			validData: async (validationData) => {
				const newFormDataValidation = {}
				innerItem.map((item) => {
					newFormDataValidation[item.name] = validationData?.[item.name]
				})
				const validationErrors = await handleFormValidation(innerItem, newFormDataValidation)
				const validationErrorsMessage = Object.fromEntries(
					Object.entries(validationErrors).filter(([key, value]) => value !== null)
				)
				const isValid = !hasValue(validationErrors)
				return {isValid: isValid, validationErrors: validationErrorsMessage}
			},
			initial: async (items) => {
				initialDynamicForm(items)
			},
		}))
		
		// Define a factory function to create the components dynamically
		function createDynamicFormItem({key, type, ...rest}) {
			
			const Component = Elements[type] || Elements.Input
			let form = {}
			let visible = true
			if (type === 'Module' || type === 'PopUp') {
				form = {
					formId: formId,
					validation: {},
					value: dynamicFormData[rest.item.name],
					defaultValues: defaultValues[rest.item.name],
					data: dynamicFormData,
					bound: dynamicFormBoundData,
					submit: (data) => handleChange(data, rest.item.name),
					change: handleChange,
					bind: (data, name) => handleBindData(data, name || rest.item.name),
					valid: handleValidateItem,
				}
				Object.keys(dynamicFormValidation).map((key) => {
					if (typeof dynamicFormValidation[key] === 'string') {
						form.validation[key] = dynamicFormValidation[key]
					}
				})
			}
			
			if (rest?.item?.condition) {
				visible = rest?.item?.condition(dynamicFormData, handleChange)
				if (!visible && !rest?.item?.saveData) handleChange('', rest.item.name)
			}
			return (
				<>
					{visible && (
						<Elements.Container key={'DynamicForm_' + key} {...rest} formId={formId}
											style={style ? style : null}>
							<Component
								{...rest}
								form={form}
								bind={handleBindData}
								formData={dynamicFormData}
								valid={handleValidateItem}
								formId={formId}
							/>
						</Elements.Container>
					)}
				</>
			)
		}
		
		// Render the dynamic form
		return (
			<>
				{innerItem.map((item, index) => {
					const value = dynamicFormData[item.name];
					const force = forceItemToUpdate[item.name] || 0;
					let validationMessage = dynamicFormValidation[item.name] || '';
					validationMessage = typeof validationMessage === 'string' ? validationMessage : '';
					
					return (
						<React.Fragment key={index}>
							{createDynamicFormItem({
								key: `DynamicFormKey_${item.name}_${index}`,
								type: item.type,
								value,
								force,
								validationMessage,
								item: {...item, size: item.size || size},
								handleChange,
								index,
							})}
						</React.Fragment>
					);
				})}
			</>
		);
		
		
	})
)

export default DynamicForm
